import { Inject, Injectable, Optional } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { delay, forkJoin, map, of } from 'rxjs';
import {
  ApiSearch,
  StorageApiSearch,
} from 'src/app/api/services/api-utils.service';
import { FolderItem } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import {
  FolderService,
  HasFolderStructure,
} from 'src/app/shared/services/folder.service';
import {
  BatchActionHandlerService,
  ListOperation,
  ListOperations,
} from './batch-action-handler.service';
import {
  ListAction,
  ListErrorHandlerService,
} from './list-error-handler.service';
import { ListSearchStateService } from './list-search-state.service';
import {
  ListItem,
  ScrollingListStateService,
} from './scrolling-list-state.service';
import {
  StorageFile,
  StorageFilesDetails,
  StorageUtilsService,
} from 'src/app/storage/services/storage-utils.service';
import { ListItemKind } from './scrolling-list-state.service';

@Injectable()
export class PaginatedScrollingListService<
  T extends { folderId?: string; id?: string },
> {
  list = toSignal(this.listState.list());
  activeOperation: ListOperation<T> | null;
  private readonly kind: ListItemKind;
  processFileApiResponse: (response: WithListFileResponse) => {
    items: ListItem<StorageFile>[];
    filesDetails: StorageFilesDetails;
    folders?: undefined;
    cursor?: undefined;
    nextPageToken?: undefined;
  };

  constructor(
    private readonly listState: ScrollingListStateService<T>,
    @Inject('WithListService')
    private readonly itemsService: WithList<T>,
    private readonly foldersService: FolderService,
    private readonly searchState: ListSearchStateService,
    @Inject('ListKind') kind: ListItemKind,
    private readonly listErrorHandler: ListErrorHandlerService,
    @Inject(BatchActionHandlerService)
    private readonly batchActionHandler: BatchActionHandlerService,
    private readonly userState: UserStateService,
    @Optional()
    @Inject('HasFolderStructure')
    private readonly folderStructureService: HasFolderStructure<T> | null,
  ) {
    this.kind = kind;
    this.searchState.init({
      folderKind: kind,
      query: undefined,
      includeSubcompanies: false,
      sortBy: 'changeDate',
      reverse: true,
      folderId: null,
      count: this.listState.maxCount,
    });
    this.listState.setName(kind + 's');
    this.init();
  }

  private init() {
    this.searchState
      .searchChanged()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (search) => {
          if (this.listState.isLoadingItems()) return;
          this.listState.clear();
          this.updateList(null, search);
        },
      });
  }

  load(storageApiSearch?: StorageApiSearch) {
    this.updateList(
      this.listState.currentCursor(),
      storageApiSearch ? storageApiSearch : this.searchState.getSearchObject(),
    );
  }

  reload(storageApiSearch?: StorageApiSearch) {
    this.listState.clear();
    this.updateList(
      null,
      storageApiSearch ? storageApiSearch : this.searchState.getSearchObject(),
    );
  }

  setOperations(listOperations: ListOperations<T>) {
    if (!listOperations || !listOperations.operations?.length) {
      return;
    }
    this.batchActionHandler.init(listOperations);

    listOperations.operations.forEach((operation) => {
      if (operation.submenu && operation.children?.length) {
        operation.children.forEach((child) => {
          if (child.separator || child.uiOnly) {
            return;
          }
          this.updateSelectedAction(child);
        });
      } else if (!operation.submenu) {
        this.updateSelectedAction(operation);
      }
    });
  }

  private updateSelectedAction(operation: ListOperation<T>) {
    const execute = this.getExecuteAction(operation);

    operation.onClick = async (
      skipBeforeBatchAction?: boolean,
      selection?: ListItem<T>[],
      args?: any,
      storageApiSearch?: StorageApiSearch,
    ) => {
      let selected = selection || this.listState.getSelected();

      if (!selected.length) {
        return;
      }

      if (operation.filter) {
        selected = operation.deepFilter
          ? this.filterSelected(
              (await this.getItemsFromFolders(
                selected as ListItem<T>[],
              )) as ListItem<T>[],
              operation.filter,
            )
          : this.filterSelected(selected as ListItem<T>[], operation.filter);
      }
      let batchSelected: any[] = [...selected];

      if (operation.groupBy) {
        batchSelected = this.groupBySelected(batchSelected, operation.groupBy);
      }

      if (!batchSelected.length) {
        this.listState.setError({
          apiError:
            'None of the selected items can run this action. Check that they are online, meet the player requirements, and any other conditions for this operation.',
          errorMessage: 'No eligible items',
        });
        return;
      }

      if (!execute) {
        if (operation.beforeBatchAction && !skipBeforeBatchAction) {
          await operation.beforeBatchAction(selected as ListItem<T>[]);
        }
        return;
      }

      this.listState.clearMessages();
      operation.inProgress = true;
      this.batchActionHandler
        .finished()
        .pipe(
          delay(2000), // Allow time for UI feedback before clearing operation state
        )
        .subscribe((finished) => {
          operation.inProgress = false;
          this.activeOperation = null;
          if (finished.success && operation.reloadList) {
            this.reload(storageApiSearch);
          } else if (
            finished.success === false &&
            !this.listState.currentError()
          ) {
            this.listState.setError({
              apiError: this.listErrorHandler.processErrorCode(
                this.listState.getName(),
                operation.name.toLowerCase() as ListAction,
                finished.error,
              ),
              errorMessage: finished.error?.message,
            });
          }
        });
      if (operation.beforeBatchAction && !skipBeforeBatchAction) {
        this.listState.setLoadingItems(true);
        operation
          .beforeBatchAction(selected as ListItem<T>[])
          .then((beforeArgs) => {
            this.activeOperation = operation;
            return this.batchActionHandler.batch(
              batchSelected,
              execute,
              operation,
              beforeArgs,
            );
          })
          .finally(() => this.listState.setLoadingItems(false));
      } else if (args) {
        this.activeOperation = operation;
        this.batchActionHandler.batch(batchSelected, execute, operation, args);
      } else {
        this.activeOperation = operation;
        this.batchActionHandler.batch(batchSelected, execute, operation);
      }
    };
  }

  private groupBySelected(
    selected: ListItem<T>[],
    groupBy: ListOperation<T>['groupBy'],
  ) {
    if (typeof groupBy === 'boolean') {
      return [{ items: selected.map((item) => item.item) }];
    }

    if (typeof groupBy === 'string') {
      const grouped = selected.reduce(
        (acc, item) => {
          const key = item.item[groupBy];
          if (!acc[key as string]) {
            acc[key as string] = [];
          }
          acc[key as string].push(item);
          return acc;
        },
        {} as Record<keyof (T | FolderItem), ListItem<T>[]>,
      );

      return Object.keys(grouped).map((key) => ({
        [groupBy]: key,
        items: grouped[key] as ListItem<T>[],
      }));
    }
  }

  private filterSelected(
    selected: ListItem<T>[],
    filter: (item: ListItem<T>) => boolean,
  ) {
    return selected.filter(filter);
  }

  private getExecuteAction(operation: ListOperation<T>) {
    if (!operation.actionCall) {
      return null;
    }

    const originalAction = operation.actionCall;

    return (item: any, args: unknown) => {
      return originalAction(item, args)
        .then(() => {
          if (operation.isDelete) {
            this.listState.remove(item);
          }
        })
        .catch((e) => {
          if (this.listState.currentError()) return;

          let apiError = e?.message;
          if (apiError) {
            if (
              e.category &&
              StorageUtilsService.STORAGE_CLIENT_ERROR[e.category]?.[apiError]
            ) {
              apiError =
                StorageUtilsService.STORAGE_CLIENT_ERROR[e.category][apiError];
            } else if (StorageUtilsService.STORAGE_CLIENT_ERROR[apiError]) {
              apiError = StorageUtilsService.STORAGE_CLIENT_ERROR[apiError];
            }
          }

          this.listState.setError({
            errorMessage: 'Something went wrong.',
            apiError,
            errorCategory: e?.category,
          });
        });
    };
  }

  private updateList(
    cursor: string | null,
    search: ApiSearch | StorageApiSearch,
  ) {
    if (this.listState.isLoadingItems() || this.listState.isEndOfList()) return;
    this.listState.setLoadingItems(true);
    const shouldLoadFolders =
      !cursor && !this.userState.isAssignedPublisher() && this.kind !== 'file';

    return forkJoin([
      shouldLoadFolders
        ? this.foldersService.loadFolders(search.folderKind, {
            ...search,
            folderId: search.folderId || undefined,
          })
        : of([]),
      shouldLoadFolders
        ? this.foldersService.loadFolderPath(search.folderId)
        : of(undefined),
      this.itemsService.list(
        this.kind === 'file' || !this.userState.isAssignedPublisher()
          ? search
          : this.getSearchForAssignedPublisher(search),
        String(cursor),
      ),
    ])
      .pipe(
        map(([folders, _folderPath, result]) => {
          if (this.kind === 'file') {
            if (!this.processFileApiResponse) {
              throw new Error(
                'processFileApiResponse must be set in the component',
              );
            }
            return this.processFileApiResponse(result as WithListFileResponse);
          } else {
            return {
              folders: folders.filter(
                (folder) =>
                  this.foldersService.showFolderItem(
                    search.folderId || FoldersApiService.ROOT_FOLDER_ID,
                    folder.parentId,
                  ) ||
                  (!search.folderId && search.query),
              ),
              items: this.userState.isAssignedPublisher()
                ? (result as WithListItemResponse<T>).items
                : (result as WithListItemResponse<T>).items.filter(
                    (item) =>
                      this.foldersService.showFolderItem(
                        search.folderId || FoldersApiService.ROOT_FOLDER_ID,
                        item.folderId,
                      ) ||
                      (!search.folderId && search.query),
                  ),
              cursor: (result as WithListItemResponse<T>).cursor,
              nextPageToken: (result as WithListItemResponse<T>).nextPageToken,
              filesDetails: undefined,
            };
          }
        }),
      )
      .subscribe({
        next: ({ folders, items, cursor, nextPageToken, filesDetails }) => {
          if (this.kind === 'file') {
            this.listState.add(items as ListItem<StorageFile>[], null);
            this.listState.filesDetails = filesDetails;
          } else {
            this.listState.add(
              [...folders, ...items].map((item) =>
                this.mapItem(item as T | FolderItem),
              ),
              cursor || nextPageToken,
            );
          }
        },
        error: (e) => {
          this.listState.setError({
            apiError: this.listErrorHandler.processErrorCode(
              this.listState.getName(),
              'load',
              e,
            ),
            errorMessage: 'Failed to load ' + this.listState.getName() + '.',
          });
          this.listState.setLoadingItems(false);
        },
        complete: () => this.listState.setLoadingItems(false),
      });
  }

  private async getItemsFromFolders(listItems: ListItem<T>[]) {
    const folders = listItems.filter((item) => item.isFolder);
    const items = listItems
      .filter((item) => !item.isFolder)
      .map((item) => item.item as T);
    this.listState.setLoadingItems(true);
    const subfolders =
      folders.length > 0 && this.folderStructureService
        ? await Promise.all(
            folders.map((folder) =>
              this.foldersService.getItemsFromFolderAndSubfolders(
                folder.item.id,
                this.folderStructureService,
              ),
            ),
          )
        : [];
    this.listState.setLoadingItems(false);
    return [...items, ...subfolders.flat()].map((item) =>
      this.mapItem(item as T | FolderItem),
    );
  }

  mapItem(item: T | FolderItem) {
    return {
      item,
      isFolder: !!(item as FolderItem).isFolder,
      selected: false,
    } as ListItem<T>;
  }

  private getSearchForAssignedPublisher(search: ApiSearch) {
    const { folderId, ...rest } = search;
    return rest;
  }
}

export interface WithListItemResponse<T> {
  items?: T[];
  cursor: string | null;
  nextPageToken: string | null;
}

export interface WithListFileResponse extends StorageFilesDetails {
  files?: StorageFile[];
  serviceAssets?: any;
  result?: any;
}

export interface WithList<T> {
  list(
    apiSearch: ApiSearch,
    cursor?: string,
  ): Promise<WithListItemResponse<T> | WithListFileResponse>;
}
